/**
 * ONERUS Custom Theme JavaScript
 * Native ES6, zero dependencies, responsive interactions.
 */

// Toast notification helper
function showToast(message) {
  // Remove existing toast if present
  const existing = document.querySelector('.toast-notification');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = 'toast-notification';
  toast.textContent = message;
  document.body.appendChild(toast);

  // Auto-remove after 3 seconds
  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transition = 'opacity 0.3s ease';
    setTimeout(() => toast.remove(), 300);
  }, 3000);
}

// ----------------------------------------------------
// Drawer Manager
// ----------------------------------------------------
class DrawerManager {
  constructor() {
    this.drawers = {
      cart: document.querySelector('#CartDrawer'),
      search: document.querySelector('#SearchDrawer'),
      mobile: document.querySelector('#MobileNavDrawer')
    };
    this.overlays = document.querySelectorAll('.drawer-overlay');
    this.closeButtons = document.querySelectorAll('[data-close-drawer]');
    
    this.init();
  }

  init() {
    // Attach close listeners
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.closeAll());
    });

    this.overlays.forEach(overlay => {
      overlay.addEventListener('click', () => this.closeAll());
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeAll();
    });

    // Handle trigger buttons
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-open-drawer]');
      if (!trigger) return;
      
      e.preventDefault();
      const drawerType = trigger.getAttribute('data-open-drawer');
      this.open(drawerType);
    });
  }

  open(type) {
    this.closeAll();
    const drawer = this.drawers[type];
    if (drawer) {
      drawer.classList.add('open');
      document.documentElement.classList.add('drawer-open');
    }
  }

  closeAll() {
    Object.values(this.drawers).forEach(drawer => {
      if (drawer) drawer.classList.remove('open');
    });
    document.documentElement.classList.remove('drawer-open');
  }
}

// ----------------------------------------------------
// AJAX Cart Handler
// ----------------------------------------------------
class AjaxCart {
  constructor() {
    this.cartDrawerInner = document.querySelector('#CartDrawerItems');
    this.cartCountBadges = document.querySelectorAll('[data-cart-count]');
    this.cartTotalAmount = document.querySelector('[data-cart-total]');
    
    this.init();
  }

  init() {
    this.initGlobalCartInterceptors();

    // Intercept standard add-to-cart clicks on product details page inline in product.liquid to preempt other scripts

    // Fallback standard submit listener for non-addons forms
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form.action.includes('/cart/add')) return;
      
      if (window.bypassAddonInterceptor) return; // Let Shiprocket submit natively!

      const hasAddons = document.querySelector('#EnablePersonalizationCheckbox') || document.querySelector('#EnableGiftCheckbox') || document.querySelector('.comp-product-checkbox:checked');
      if (hasAddons && (form.id === 'ProductMainForm' || form.id === 'ProductBuyNowForm')) {
        e.preventDefault();
        return;
      }
      
      e.preventDefault();
      this.addFormItem(form, form.querySelector('[type="submit"]'));
    });

    // Handle item removals and quantity changes inside the cart drawer
    if (this.cartDrawerInner) {
      this.cartDrawerInner.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-cart-update]');
        if (!btn) return;
        
        e.preventDefault();
        const line = btn.getAttribute('data-line');
        const qty = parseInt(btn.getAttribute('data-qty'), 10);
        this.updateItemQuantity(line, qty);
      });
    }

    // Load initial cart details
    this.refreshCartDetails();
  }

  initGlobalCartInterceptors() {
    // Intercept window.fetch requests
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      const url = args[0];
      
      if (typeof url === 'string' && (
        url.includes('/cart/add') || 
        url.includes('/cart/change') || 
        url.includes('/cart/update') || 
        url.includes('/cart/clear')
      )) {
        setTimeout(() => {
          this.refreshCartDetails();
        }, 500); // 500ms delay to clear Shopify DB write replication lag
      }
      return response;
    };

    // Intercept window.XMLHttpRequest requests (commonly used by older libs/SDKs)
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    const self = this;
    
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this._cartUrl = url;
      return originalOpen.apply(this, [method, url, ...rest]);
    };

    XMLHttpRequest.prototype.send = function(...args) {
      this.addEventListener('load', () => {
        if (typeof this._cartUrl === 'string' && (
          this._cartUrl.includes('/cart/add') || 
          this._cartUrl.includes('/cart/change') || 
          this._cartUrl.includes('/cart/update') || 
          this._cartUrl.includes('/cart/clear')
        )) {
          setTimeout(() => {
            self.refreshCartDetails();
          }, 500); // 500ms delay to clear Shopify DB write replication lag
        }
      });
      return originalSend.apply(this, args);
    };
  }

  async addFormItem(form, submitBtn, isShiprocketBuyNow = false) {
    let originalBtnHTML = '';
    
    if (submitBtn && !isShiprocketBuyNow) {
      originalBtnHTML = submitBtn.innerHTML;
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<i class="material-symbols-outlined ph-light ph-spinner-gap ph-spin" style="font-size:18px;"></i>';
    }

    try {
      const variantInput = form.querySelector('[name="id"]');
      const qtyInput = form.querySelector('[name="quantity"]');
      const quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      const mainVariantId = variantInput ? variantInput.value : '';

      const formData = new FormData();
      let itemIndex = 0;

      // 1. Check for Custom Name Personalization Add-on
      const personalizationCheckbox = document.querySelector('#EnablePersonalizationCheckbox');
      const nameInput = document.querySelector('#PersonalizationNameInput');
      const personalizationName = nameInput ? nameInput.value.trim() : '';


      if (personalizationCheckbox && personalizationCheckbox.checked) {
        if (!personalizationName) {
          showToast('Please enter a name for personalization.');
          if (submitBtn && !isShiprocketBuyNow) {
            submitBtn.disabled = false;
            submitBtn.innerHTML = originalBtnHTML;
          }
          return; // Stop form submission
        }

        const personalizationVariantId = personalizationCheckbox.getAttribute('data-variant-id');
        const productTitleEl = document.querySelector('.product-page-title');
        const productTitle = productTitleEl ? productTitleEl.textContent.trim() : 'Backpack';

        formData.append(`items[${itemIndex}][id]`, personalizationVariantId);
        formData.append(`items[${itemIndex}][quantity]`, quantity);
        formData.append(`items[${itemIndex}][properties][Printed Name]`, personalizationName);
        formData.append(`items[${itemIndex}][properties][For Product]`, productTitle);
        itemIndex++;
      }

      // 2. Check for Gift Backpack Add-on
      const giftCheckbox = document.querySelector('#EnableGiftCheckbox');
      if (giftCheckbox && giftCheckbox.checked) {
        const giftVariantId = giftCheckbox.getAttribute('data-variant-id');
        formData.append(`items[${itemIndex}][id]`, giftVariantId);
        formData.append(`items[${itemIndex}][quantity]`, '1');
        itemIndex++;
      }

      // 3. Check for selected Complementary Products
      const selectedCompCheckboxes = document.querySelectorAll('.comp-product-checkbox:checked');
      let compError = false;

      selectedCompCheckboxes.forEach(checkbox => {
        const card = checkbox.closest('.comp-product-card');
        const variantId = checkbox.getAttribute('data-variant-id');

        formData.append(`items[${itemIndex}][id]`, variantId);
        formData.append(`items[${itemIndex}][quantity]`, '1');

        if (card) {
          const compNameInput = card.querySelector('.comp-personalization-input');
          if (compNameInput) {
            const printedName = compNameInput.value.trim();
            if (!printedName) {
              showToast('Please enter a name for personalization.');
              compError = true;
            } else {
              const productTitleEl = document.querySelector('.product-page-title');
              const productTitle = productTitleEl ? productTitleEl.textContent.trim() : 'Backpack';
              formData.append(`items[${itemIndex}][properties][Printed Name]`, printedName);
              formData.append(`items[${itemIndex}][properties][For Product]`, productTitle);
            }
          }
        }
        itemIndex++;
      });

      if (compError) {
        if (submitBtn && !isShiprocketBuyNow) {
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalBtnHTML;
        }
        return; // Stop form submission
      }

      if (isShiprocketBuyNow) {
        const shiprocketProducts = this.buildShiprocketCheckoutProducts(form, quantity);
        if (shiprocketProducts.length === 0) {
          throw new Error('No Shiprocket checkout products could be built');
        }

        this.triggerShiprocketDirectCheckout(shiprocketProducts);
        return;
      }

      if (itemIndex > 0) {
        // Add only the add-on products to the cart first via AJAX.
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) throw new Error('Add-on addition failed');
        await response.json();
      }

      // Wait 600ms for Shopify DB sync, then re-click the original button with bypass
      // so native handlers (Shiprocket for Buy Now, or form submit for Add to Cart)
      // process the main product while add-ons are already in the cart
      setTimeout(() => {
        window.bypassAddonInterceptor = true;

        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalBtnHTML;
          submitBtn.click();
        }

        // Reset the bypass flag after a short delay
        setTimeout(() => {
          window.bypassAddonInterceptor = false;
        }, 1000);
      }, 600);

    } catch (err) {
      console.error('[AddCartError]:', err);
      showToast('Could not add item. Try again.');
      if (submitBtn && !isShiprocketBuyNow) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalBtnHTML;
      }
    }
  }

  getProductPageData() {
    const productJson = document.getElementById('ProductPageJsonData');
    if (!productJson) return null;

    try {
      return JSON.parse(productJson.textContent);
    } catch (error) {
      console.error('[Shiprocket] Failed to parse product JSON:', error);
      return null;
    }
  }

  getShiprocketProductMeta() {
    const metaJson = document.getElementById('ShiprocketProductMetaData');
    if (!metaJson) return null;

    try {
      return JSON.parse(metaJson.textContent);
    } catch (error) {
      console.error('[Shiprocket] Failed to parse product meta JSON:', error);
      return null;
    }
  }

  createShiprocketCheckoutProduct({
    productId,
    title,
    variantId,
    variantTitle = '',
    price,
    quantity,
    image,
    vendor = '',
    productType = '',
    properties = {}
  }) {
    return {
      productId: productId ? Number(productId) : null,
      title: title || '',
      variantId: variantId ? Number(variantId) : null,
      variantTitle: variantTitle === 'Default Title' ? '' : (variantTitle || ''),
      price: Number(price) || 0,
      quantity: Number(quantity) || 1,
      image: image || '',
      item_meta_data: { properties },
      customAttributes: properties,
      vendor,
      product_type: productType
    };
  }

  buildShiprocketCheckoutProducts(form, quantity) {
    const products = [];
    const productData = this.getProductPageData();
    const productMeta = this.getShiprocketProductMeta();
    const mainVariantId = form.querySelector('[name="id"]')?.value;
    const mainVariant = productData?.variants?.find((variant) => String(variant.id) === String(mainVariantId));

    if (mainVariant) {
      products.push(this.createShiprocketCheckoutProduct({
        productId: productMeta?.productId,
        title: productMeta?.title,
        variantId: mainVariant.id,
        variantTitle: mainVariant.title,
        price: (mainVariant.price || 0) / 100,
        quantity,
        image: mainVariant.image || productMeta?.image,
        vendor: productMeta?.vendor,
        productType: productMeta?.productType
      }));
    }

    const personalizationCheckbox = document.querySelector('#EnablePersonalizationCheckbox');
    const personalizationName = document.querySelector('#PersonalizationNameInput')?.value.trim() || '';
    if (personalizationCheckbox?.checked) {
      products.push(this.createShiprocketCheckoutProduct({
        productId: personalizationCheckbox.dataset.productId,
        title: personalizationCheckbox.dataset.productTitle,
        variantId: personalizationCheckbox.dataset.variantId,
        price: personalizationCheckbox.dataset.productPrice,
        quantity,
        image: personalizationCheckbox.dataset.productImage,
        vendor: personalizationCheckbox.dataset.productVendor,
        productType: personalizationCheckbox.dataset.productType,
        properties: personalizationName ? {
          'Printed Name': personalizationName,
          'For Product': productMeta?.title || 'Backpack'
        } : {}
      }));
    }

    const giftCheckbox = document.querySelector('#EnableGiftCheckbox');
    if (giftCheckbox?.checked) {
      products.push(this.createShiprocketCheckoutProduct({
        productId: giftCheckbox.dataset.productId,
        title: giftCheckbox.dataset.productTitle,
        variantId: giftCheckbox.dataset.variantId,
        price: giftCheckbox.dataset.productPrice,
        quantity: 1,
        image: giftCheckbox.dataset.productImage,
        vendor: giftCheckbox.dataset.productVendor,
        productType: giftCheckbox.dataset.productType
      }));
    }

    const selectedCompCheckboxes = document.querySelectorAll('.comp-product-checkbox:checked');
    for (const checkbox of selectedCompCheckboxes) {
      const properties = {};
      const card = checkbox.closest('.comp-product-card');
      const compNameInput = card?.querySelector('.comp-personalization-input');
      const printedName = compNameInput?.value.trim() || '';

      if (printedName) {
        properties['Printed Name'] = printedName;
        properties['For Product'] = productMeta?.title || 'Backpack';
      }

      products.push(this.createShiprocketCheckoutProduct({
        productId: checkbox.dataset.productId,
        title: checkbox.dataset.productTitle,
        variantId: checkbox.dataset.variantId,
        variantTitle: checkbox.dataset.variantTitle,
        price: checkbox.dataset.productPrice,
        quantity: 1,
        image: checkbox.dataset.productImage,
        vendor: checkbox.dataset.productVendor,
        productType: checkbox.dataset.productType,
        properties
      }));
    }

    return products.filter((product) => product.variantId);
  }

  triggerShiprocketDirectCheckout(products) {
    window.bypassAddonInterceptor = true;

    const payload = {
      type: 'product',
      products,
      fallbackUrl: window.location.href
    };

    try {
      if (window.shiprocketCheckoutEvents?.buyDirect) {
        window.shiprocketCheckoutEvents.buyDirect(payload);
        return;
      }

      if (typeof window.shiprocketCheckoutDirectHandler === 'function') {
        window.shiprocketCheckoutDirectHandler(payload);
        return;
      }

      throw new Error('Shiprocket direct checkout API is unavailable');
    } finally {
      setTimeout(() => {
        window.bypassAddonInterceptor = false;
      }, 1500);
    }
  }

  async updateItemQuantity(line, qty) {
    try {
      if (this.cartDrawerInner) {
        this.cartDrawerInner.style.opacity = '0.5';
        this.cartDrawerInner.style.pointerEvents = 'none';
      }
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: parseInt(line, 10), quantity: qty })
      });

      if (!response.ok) throw new Error('Quantity update failed');
      
      await this.refreshCartDetails();
    } catch (err) {
      console.error(err);
      showToast('Could not update quantity.');
    } finally {
      if (this.cartDrawerInner) {
        this.cartDrawerInner.style.opacity = '1';
        this.cartDrawerInner.style.pointerEvents = 'auto';
      }
    }
  }

  async refreshCartDetails() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      
      // Update count badges
      this.cartCountBadges.forEach(badge => {
        badge.textContent = cart.item_count;
        if (cart.item_count > 0) {
          badge.style.display = 'flex';
        } else {
          badge.style.display = 'none';
        }
      });

      // Update total price amount
      if (this.cartTotalAmount) {
        this.cartTotalAmount.textContent = this.formatPrice(cart.total_price);
      }

      // Repopulate HTML items in Drawer
      if (this.cartDrawerInner) {
        if (cart.item_count === 0) {
          this.cartDrawerInner.innerHTML = `
            <div class="empty-cart-state">
              <i class="material-symbols-outlined ph-light ph-shopping-bag"></i>
              <p>Your shopping bag is currently empty.</p>
              <a href="/collections/all" class="btn-primary" data-close-drawer>Shop All Gear</a>
            </div>
          `;
          return;
        }

        let html = '';
        cart.items.forEach((item, index) => {
          const lineId = index + 1;
          
          // Variant options check (hide if 'Default Title')
          const variantTitleHtml = item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-item__options font-sans text-[12px] text-mute mt-[2px]">${item.variant_title}</p>`
            : '';
          
          // Line level discount check
          const hasLineDiscount = item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0;
          const promoHtml = hasLineDiscount
            ? `<div class="cart-item__promo">
                <i class="material-symbols-outlined ph-light ph-tag"></i>
                <span>Promo Applied</span>
               </div>`
            : '';

          // Compare original line price vs final line price
          const comparePriceHtml = hasLineDiscount 
            ? `<span class="font-sans text-[11px] text-mute line-through">${this.formatPrice(item.original_line_price)}</span>` 
            : '';
          
          const priceClass = hasLineDiscount ? 'text-green-700 font-bold' : 'text-ink';
          
          html += `
            <div class="cart-item relative group font-sans">
              <div class="cart-item__image">
                <img src="${item.image}" alt="${item.product_title}">
              </div>
              <div class="cart-item__details flex-1 flex flex-col justify-between">
                <div>
                  <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
                  ${variantTitleHtml}
                  ${promoHtml}
                </div>
                
                <div class="cart-item__price-qty flex justify-between items-end mt-sm">
                  <div class="cart-item__quantity-selector">
                    <button type="button" data-cart-update data-line="${lineId}" data-qty="${item.quantity - 1}">
                      <span class="qty-btn-label">&minus;</span>
                    </button>
                    <span class="qty-val">${item.quantity}</span>
                    <button type="button" data-cart-update data-line="${lineId}" data-qty="${item.quantity + 1}">
                      <span class="qty-btn-label">&#43;</span>
                    </button>
                  </div>
                  <div class="cart-item__price">
                    ${comparePriceHtml}
                    <span class="${priceClass}">${this.formatPrice(item.final_line_price)}</span>
                  </div>
                </div>
              </div>

              <!-- Remove Button - hover on desktop, always visible on mobile -->
              <div class="cart-item__remove-btn">
                <button type="button" data-cart-update data-line="${lineId}" data-qty="0">Remove</button>
              </div>
            </div>
          `;
        });
        
        this.cartDrawerInner.innerHTML = html;
        
        // Re-attach data-close-drawer event to links inside drawer
        this.cartDrawerInner.querySelectorAll('[data-close-drawer]').forEach(el => {
          el.addEventListener('click', () => window.drawerManager.closeAll());
        });
      }

      // Dispatch cart:updated event for external SDKs (Shiprocket, etc.)
      window.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (err) {
      console.error('[AjaxCart] Failed to refresh cart:', err);
    }
  }

  formatPrice(cents) {
    const amount = cents / 100;
    return `Rs. ${Math.round(amount).toLocaleString('en-IN')}`;
  }
}

// ----------------------------------------------------
// Wishlist Handler (LocalStorage based)
// ----------------------------------------------------
class WishlistHandler {
  constructor() {
    this.storageKey = 'onerus_wishlist';
    this.wishlistCountBadges = document.querySelectorAll('[data-wishlist-count]');
    this.init();
  }

  init() {
    // Add click interception for save-to-wishlist buttons
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      
      e.preventDefault();
      const productId = btn.getAttribute('data-wishlist-toggle');
      this.toggle(productId, btn);
    });

    this.updateBadges();
    this.syncButtonStates();
  }

  getWishlist() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  toggle(productId, buttonElement) {
    let list = this.getWishlist();
    const ids = list.map(item => String(typeof item === 'object' ? item.id : item));
    const exists = ids.includes(String(productId));

    if (exists) {
      list = list.filter(item => {
        const itemId = String(typeof item === 'object' ? item.id : item);
        return itemId !== String(productId);
      });
      showToast('Removed from Wishlist');
    } else {
      let productData = null;
      const card = buttonElement.closest('[data-product-card]');
      if (card) {
        const jsonScript = card.querySelector('.product-card-json');
        if (jsonScript) {
          try {
            const raw = JSON.parse(jsonScript.textContent);
            const cardImg = card.querySelector('[data-product-card-image]') || card.querySelector('.product-image__img');
            const displayImg = cardImg ? cardImg.src : raw.image;
            
            const activeSwatch = card.querySelector('[data-swatch-value].swatch-dot-active');
            const activeColor = activeSwatch ? activeSwatch.getAttribute('data-swatch-value') : '';
            const selectedVariant = raw.variants.find(v => v.options.includes(activeColor)) || raw.variants[0];

            productData = {
              id: raw.id,
              handle: raw.handle,
              title: raw.title,
              image: displayImg,
              price: `Rs. ${Math.round(raw.price / 100).toLocaleString('en-IN')}`,
              type: raw.type,
              raw: raw,
              selectedVariantId: selectedVariant ? selectedVariant.id : ''
            };
          } catch {}
        }
      } else {
        const pageJson = document.getElementById('ProductPageJsonData');
        const titleEl = document.querySelector('.product-page-title');
        const priceEl = document.getElementById('ProductPagePrice');
        const typeEl = document.querySelector('.product-type-badge');
        
        if (pageJson && titleEl && priceEl) {
          try {
            const raw = JSON.parse(pageJson.textContent);
            
            // Get selected variant from input
            const variantIdInput = document.getElementById('ProductVariantIdInput');
            const currentVariantId = variantIdInput ? variantIdInput.value : '';
            const selectedVariant = raw.variants.find(v => String(v.id) === String(currentVariantId)) || raw.variants[0];
            
            // Resolve image for the selected variant
            let variantImg = '';
            if (selectedVariant && selectedVariant.image) {
              variantImg = selectedVariant.image;
            } else if (raw.images && raw.images[0]) {
              variantImg = raw.images[0].url || raw.images[0];
            }

            let displayTitle = titleEl.textContent.trim();
            if (selectedVariant && selectedVariant.title && selectedVariant.title !== 'Default Title') {
              displayTitle += ` - ${selectedVariant.title}`;
            }

            productData = {
              id: raw.id || productId,
              handle: window.location.pathname.split('/').pop(),
              title: displayTitle,
              image: variantImg,
              price: priceEl.textContent.trim(),
              type: typeEl ? typeEl.textContent.trim() : 'GEAR',
              raw: raw,
              selectedVariantId: selectedVariant ? selectedVariant.id : ''
            };
          } catch {}
        }
      }

      list.push(productData || { id: productId });
      showToast('Added to Wishlist');
    }

    localStorage.setItem(this.storageKey, JSON.stringify(list));
    
    this.updateBadges();
    this.syncButtonStates();
    
    // Dispatch event for components (like Wishlist Page) to catch
    document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { wishlist: list } }));
  }

  updateBadges() {
    const list = this.getWishlist();
    const count = list.length;

    this.wishlistCountBadges.forEach(badge => {
      badge.textContent = count;
      if (count > 0) {
        badge.style.display = 'flex';
      } else {
        badge.style.display = 'none';
      }
    });
  }

  syncButtonStates() {
    const list = this.getWishlist();
    const ids = list.map(item => String(typeof item === 'object' ? item.id : item));
    const buttons = document.querySelectorAll('[data-wishlist-toggle]');
    
    buttons.forEach(btn => {
      const pid = String(btn.getAttribute('data-wishlist-toggle'));
      const inWishlist = ids.includes(pid);
      const icon = btn.querySelector('.material-symbols-outlined');
      const labelSpan = btn.querySelector('[data-wishlist-label]');
      
      if (inWishlist) {
        btn.classList.add('wishlisted');
        if (icon) {
          icon.classList.remove('ph-light');
          icon.classList.add('ph-fill');
          icon.style.color = '#d30005';
        }
        if (labelSpan) labelSpan.textContent = 'SAVED TO WISHLIST';
      } else {
        btn.classList.remove('wishlisted');
        if (icon) {
          icon.classList.remove('ph-fill');
          icon.classList.add('ph-light');
          icon.style.color = '';
        }
        if (labelSpan) labelSpan.textContent = 'SAVE TO WISHLIST';
      }
    });
  }
}

// ----------------------------------------------------
// Swatches & Product Interactions
// ----------------------------------------------------
class ProductCardComponent {
  constructor() {
    this.init();
  }

  init() {
    // Swatch hover/click handlers on product grids
    document.addEventListener('click', (e) => {
      const swatch = e.target.closest('[data-swatch-value]');
      if (!swatch) return;
      
      e.preventDefault();
      const value = swatch.getAttribute('data-swatch-value');
      const card = swatch.closest('.product-card-hover, .product-card-wrapper, [data-product-card]');
      if (!card) return;
      
      // Update active swatch
      card.querySelectorAll('[data-swatch-value]').forEach(s => {
        s.classList.remove('swatch-dot-active');
        s.classList.add('swatch-dot');
      });
      swatch.classList.add('swatch-dot-active');
      swatch.classList.remove('swatch-dot');
      
      // Swap thumbnail image
      const imageEl = card.querySelector('.product-image__img.active') || card.querySelector('.product-image__img') || card.querySelector('[data-product-card-image]');
      const variantImg = swatch.getAttribute('data-variant-img');
      if (imageEl && variantImg) {
        imageEl.src = variantImg;
      }
    });
  }
}

// ----------------------------------------------------
// Accordion Toggle
// ----------------------------------------------------
class AccordionToggle {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-accordion-trigger]');
      if (!btn) return;
      
      e.preventDefault();
      const parent = btn.closest('.border-b');
      if (!parent) return;
      
      const content = parent.querySelector('.accordion-content');
      const icon = btn.querySelector('.material-symbols-outlined');
      const isOpen = content.classList.contains('open');
      
      if (isOpen) {
        content.classList.remove('open');
        btn.setAttribute('aria-expanded', 'false');
        if (icon && !icon.classList.contains('accordion-icon')) icon.style.transform = 'rotate(0deg)';
      } else {
        // Close other open accordions in the same group/parent container
        const group = btn.closest('.product-accordions-group');
        if (group) {
          group.querySelectorAll('.product-accordion-item').forEach(item => {
            if (item !== parent) {
              const otherContent = item.querySelector('.accordion-content');
              const otherBtn = item.querySelector('[data-accordion-trigger]');
              const otherIcon = otherBtn ? otherBtn.querySelector('.material-symbols-outlined') : null;
              if (otherContent && otherContent.classList.contains('open')) {
                otherContent.classList.remove('open');
                if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
                if (otherIcon && !otherIcon.classList.contains('accordion-icon')) otherIcon.style.transform = 'rotate(0deg)';
              }
            }
          });
        }

        content.classList.add('open');
        btn.setAttribute('aria-expanded', 'true');
        if (icon && !icon.classList.contains('accordion-icon')) icon.style.transform = 'rotate(180deg)';
      }
    });
  }
}

// ----------------------------------------------------
// Quick View Manager
// ----------------------------------------------------
class QuickView {
  constructor() {
    this.modal = document.querySelector('#QuickViewModal');
    if (!this.modal) return;
    
    this.closeBtns = this.modal.querySelectorAll('[data-close-modal]');
    this.optionsContainer = this.modal.querySelector('#QuickViewOptions');
    this.variantIdInput = this.modal.querySelector('#QuickViewVariantId');
    this.submitBtn = this.modal.querySelector('#QuickViewSubmit');
    this.soldOutBtn = this.modal.querySelector('#QuickViewSoldOut');
    
    this.product = null;
    this.selectedOptions = {};
    
    this.init();
  }

  init() {
    // Intercept clicks on quickview triggers
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-trigger-quickview]');
      if (!trigger) return;
      
      e.preventDefault();
      const card = trigger.closest('[data-product-card]');
      if (card) {
        this.open(card);
      }
    });

    this.closeBtns.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });
    
    // Close on overlay click
    const overlay = this.modal.querySelector('.quickview-modal__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.close());
    }
  }

  open(card) {
    const jsonScript = card.querySelector('.product-card-json');
    if (!jsonScript) return;
    
    try {
      this.product = JSON.parse(jsonScript.textContent);
      this.selectedOptions = {};
      
      // Populate static fields
      this.modal.querySelector('#QuickViewImage').src = this.product.image;
      this.modal.querySelector('#QuickViewImage').alt = this.product.title;
      this.modal.querySelector('#QuickViewTitle').textContent = this.product.title;
      this.modal.querySelector('#QuickViewTypeBadge').textContent = this.product.type;
      this.modal.querySelector('#QuickViewFullDetails').href = `/products/${this.product.handle}`;
      
      // Populate options
      this.renderOptions();
      
      // Select variant option values (match selected variant or fall back to first values)
      const selectedVariantId = card.getAttribute('data-selected-variant-id');
      const selectedVariant = selectedVariantId ? this.product.variants.find(v => String(v.id) === String(selectedVariantId)) : null;

      this.product.options.forEach((opt, idx) => {
        if (selectedVariant && selectedVariant.options && selectedVariant.options[idx]) {
          this.selectedOptions[opt.name] = selectedVariant.options[idx];
        } else if (opt.values && opt.values.length > 0) {
          // values may be {name, colorHex} objects or plain strings
          const firstVal = opt.values[0];
          this.selectedOptions[opt.name] = typeof firstVal === 'object' ? (firstVal.name || '') : firstVal;
        }
      });
      
      this.updateSelection();
      
      // Display modal
      this.modal.style.display = 'flex';
      document.documentElement.classList.add('drawer-open');
    } catch (err) {
      console.error('[QuickView] Error opening modal:', err);
    }
  }

  close() {
    this.modal.style.display = 'none';
    document.documentElement.classList.remove('drawer-open');
  }

  renderOptions() {
    this.optionsContainer.innerHTML = '';
    
    this.product.options.forEach(option => {
      if (option.values.length <= 1 && option.name.toLowerCase() === 'title') return;
      
      const optDiv = document.createElement('div');
      optDiv.className = 'quickview-option';
      
      const titleSpan = document.createElement('span');
      titleSpan.className = 'quickview-option__title';
      titleSpan.textContent = option.name;
      optDiv.appendChild(titleSpan);
      
      const valsDiv = document.createElement('div');
      valsDiv.className = 'quickview-option__values';
      
      option.values.forEach(value => {
        // values may be {name, colorHex} objects (new format) or plain strings (legacy)
        const valStr = typeof value === 'object' ? (value.name || '') : String(value);
        const valBtn = document.createElement('button');
        valBtn.type = 'button';
        valBtn.className = 'quickview-option__value';
        valBtn.textContent = valStr;
        valBtn.setAttribute('data-option-name', option.name);
        valBtn.setAttribute('data-option-value', valStr);
        
        valBtn.addEventListener('click', (e) => {
          this.selectedOptions[option.name] = valStr;
          this.updateSelection();
        });
        
        valsDiv.appendChild(valBtn);
      });
      
      optDiv.appendChild(valsDiv);
      this.optionsContainer.appendChild(optDiv);
    });
  }

  updateSelection() {
    // Sync button states
    const optionButtons = this.optionsContainer.querySelectorAll('.quickview-option__value');
    optionButtons.forEach(btn => {
      const name = btn.getAttribute('data-option-name');
      const val = btn.getAttribute('data-option-value');
      
      if (this.selectedOptions[name] === val) {
        btn.classList.add('quickview-option__value--selected');
      } else {
        btn.classList.remove('quickview-option__value--selected');
      }
    });

    // Find matching variant
    const variant = this.product.variants.find(v => {
      return v.options.every((optVal, idx) => {
        const optName = this.product.options[idx].name;
        return this.selectedOptions[optName] === optVal;
      });
    });

    if (variant) {
      this.variantIdInput.value = variant.id;
      
      // Update price
      const priceText = `Rs. ${Math.round(variant.price / 100).toLocaleString('en-IN')}`;
      this.modal.querySelector('#QuickViewPrice').textContent = priceText;
      
      const comparePriceEl = this.modal.querySelector('#QuickViewComparePrice');
      const saleBadgeEl = this.modal.querySelector('#QuickViewSaleBadge');
      
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const compPriceText = `Rs. ${Math.round(variant.compare_at_price / 100).toLocaleString('en-IN')}`;
        comparePriceEl.textContent = compPriceText;
        comparePriceEl.style.display = 'inline';
        saleBadgeEl.style.display = 'inline';
      } else {
        comparePriceEl.style.display = 'none';
        saleBadgeEl.style.display = 'none';
      }

      // Update image
      if (variant.image) {
        this.modal.querySelector('#QuickViewImage').src = variant.image;
      } else {
        this.modal.querySelector('#QuickViewImage').src = this.product.image;
      }

      // Update availability
      if (variant.available) {
        this.submitBtn.style.display = 'inline-flex';
        this.soldOutBtn.style.display = 'none';
      } else {
        this.submitBtn.style.display = 'none';
        this.soldOutBtn.style.display = 'block';
      }
    } else {
      this.variantIdInput.value = '';
      this.submitBtn.style.display = 'none';
      this.soldOutBtn.style.display = 'block';
    }
  }
}

// ----------------------------------------------------
// Guest Auth Required Modal Handler
// ----------------------------------------------------
class GuestAuthModal {
  constructor() {
    this.modal = document.querySelector('#AuthRequiredModal');
    if (!this.modal) return;

    this.closeBtns = this.modal.querySelectorAll('[data-close-auth-modal]');
    this.triggerClass = 'js-guest-auth-trigger';

    this.init();
  }

  init() {
    // Intercept clicks on guest auth triggers
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest(`.${this.triggerClass}`);
      if (!trigger) return;

      // If user is logged in, let normal navigation happen
      if (window.Shopify && window.Shopify.customerLoggedIn) {
        return;
      }

      e.preventDefault();
      this.open();
    });

    // Attach close listeners
    this.closeBtns.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });
  }

  open() {
    this.modal.style.display = 'flex';
    // Force layout / repaint
    this.modal.offsetHeight;
    this.modal.classList.add('open');
    document.documentElement.classList.add('drawer-open');
  }

  close() {
    this.modal.classList.remove('open');
    setTimeout(() => {
      if (!this.modal.classList.contains('open')) {
        this.modal.style.display = 'none';
      }
    }, 300);
    document.documentElement.classList.remove('drawer-open');
  }
}

// ----------------------------------------------------
// Product Card Image Slideshow Controller
// ----------------------------------------------------
class ProductCardSlideshow {
  constructor() {
    this.slideshows = document.querySelectorAll('[data-product-slideshow]');
    this.init();
  }

  init() {
    this.slideshows.forEach(slideshow => {
      const slides = slideshow.querySelectorAll('.product-card__slide');
      if (slides.length <= 1) return;

      let currentIdx = 0;
      setInterval(() => {
        slides[currentIdx].classList.remove('active');
        currentIdx = (currentIdx + 1) % slides.length;
        slides[currentIdx].classList.add('active');
      }, 5000);
    });
  }
}

// ----------------------------------------------------
// Page Animations & Loader
// ----------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
  // Initialize Managers
  window.drawerManager = new DrawerManager();
  window.ajaxCart = new AjaxCart();
  window.wishlistHandler = new WishlistHandler();
  window.productCardComp = new ProductCardComponent();
  window.accordionComp = new AccordionToggle();
  window.quickView = new QuickView();
  window.guestAuthModal = new GuestAuthModal();
  window.productCardSlideshow = new ProductCardSlideshow();

  // Scroll Reveal Animations (Immediately reveals elements in or near the viewport)
  const initRevealObserver = () => {
    const reveals = document.querySelectorAll('.reveal:not(.active)');
    if (!reveals.length) return;

    if ('IntersectionObserver' in window) {
      const revealObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
            observer.unobserve(entry.target);
          }
        });
      }, {
        root: null,
        rootMargin: '150px 0px 50px 0px', // Triggers even 150px before entering viewport
        threshold: 0
      });

      reveals.forEach(el => revealObserver.observe(el));
    } else {
      reveals.forEach(el => el.classList.add('active'));
    }
  };

  initRevealObserver();
  window.addEventListener('load', initRevealObserver);
  window.addEventListener('scroll', initRevealObserver, { passive: true });
});





